import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  attachBadgesToMembers,
  mapMemberBadgeRow,
  buildProfileUpdatePayload,
} from '../lib/memberHelpers';
import { mapClubFromDb, mapClubSuggestionFromDb, clubTipoToDb } from '../lib/clubHelpers';
import { mapEventoFromDb, mapRsvpFromDb, buildEventoInsertPayload } from '../lib/eventHelpers';
import { mapTrainingFromDb, mapReelFromDb } from '../lib/contentHelpers';
import {
  mapCommentFromDb,
  mapReactionFromDb,
  mapCommentReactionFromDb,
  commentReactionToDb,
} from '../lib/socialHelpers';
import {
  mapNegocioFromDb,
  makeNegocioSlug,
  buildNegocioInsertPayload,
  buildNegocioUpdatePayload,
  uploadNegocioFoto,
  NEGOCIO_MAX_FOTOS,
} from '../lib/negocioHelpers';
import { 
  RUBROS, BADGES_INFO, CATEGORIAS_ACADEMIA
} from '../mockData';

const AppContext = createContext();

async function fetchMemberBadgesForIds(memberIds) {
  if (!memberIds?.length) return [];
  const { data, error } = await supabase
    .from('member_badges')
    .select('id, member_id, is_featured, vence_at, badges(nombre)')
    .in('member_id', memberIds);
  if (error) throw error;
  return data || [];
}

async function fetchMemberByAuthUserId(authUserId) {
  const { data, error } = await supabase
    .from('members')
    .select('*')
    .eq('auth_user_id', authUserId)
    .single();

  if (error) throw error;

  const badgeRows = await fetchMemberBadgesForIds([data.id]);
  const badges = badgeRows.map(mapMemberBadgeRow).filter(Boolean);
  return { ...data, badges };
}

export const AppProvider = ({ children }) => {
  // Auth State
  const [session, setSession] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const isAuthenticated = !!session;

  // Data State — members/capitulos from Supabase; other domains still mock until their pass
  const [members, setMembers] = useState([]);
  const [capitulos, setCapitulos] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [trainings, setTrainings] = useState([]);
  const [reels, setReels] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [clubSuggestions, setClubSuggestions] = useState([]);
  const [rsvps, setRsvps] = useState([]); // Array of { eventId, memberId }

  // Social State
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [commentReactions, setCommentReactions] = useState([]);
  const [contentReports, setContentReports] = useState([]);
  const [negociosServicios, setNegociosServicios] = useState([]);

  // Voting State — candidate member id voted for in the current Abanderado cycle
  const [abanderadoVote, setAbanderadoVote] = useState(null);
  const [abanderadoCycleId, setAbanderadoCycleId] = useState(null);

  const loadDirectoryData = useCallback(async () => {
    setDataLoading(true);
    try {
      const [membersRes, capitulosRes, clubsRes, suggestionsRes, eventosRes, rsvpsRes, trainingsRes, reelsRes, commentsRes, reactionsRes, commentReactionsRes, contentReportsRes, negociosRes] = await Promise.all([
        supabase.from('members').select('*').order('nombre'),
        supabase.from('capitulos').select('*').eq('activo', true).order('nombre'),
        supabase.from('clubs').select('*').order('created_at', { ascending: false }),
        supabase
          .from('club_suggestions')
          .select('*, suggested_by_member:members!suggested_by(nombre)')
          .eq('status', 'pending')
          .order('created_at', { ascending: false }),
        supabase.from('eventos').select('*').order('fecha', { ascending: true }),
        supabase.from('rsvps').select('*'),
        supabase.from('trainings').select('*').order('created_at', { ascending: false }),
        supabase.from('reels').select('*').order('created_at', { ascending: false }),
        supabase.from('comments').select('*').order('created_at', { ascending: true }),
        supabase.from('reactions').select('*'),
        supabase.from('comment_reactions').select('*'),
        supabase.from('content_reports').select('*').order('created_at', { ascending: false }),
        supabase.from('negocios_servicios').select('*').order('created_at', { ascending: false }),
      ]);

      if (membersRes.error) throw membersRes.error;
      if (capitulosRes.error) throw capitulosRes.error;
      if (clubsRes.error) throw clubsRes.error;
      if (suggestionsRes.error) throw suggestionsRes.error;
      if (eventosRes.error) throw eventosRes.error;
      if (rsvpsRes.error) throw rsvpsRes.error;
      if (trainingsRes.error) throw trainingsRes.error;
      if (reelsRes.error) throw reelsRes.error;
      if (commentsRes.error) throw commentsRes.error;
      if (reactionsRes.error) throw reactionsRes.error;
      if (commentReactionsRes.error) throw commentReactionsRes.error;
      // content_reports SELECT is staff-only; non-staff get empty via RLS (ignore hard failures)
      if (contentReportsRes.error) {
        console.warn('content_reports load skipped:', contentReportsRes.error.message);
      }
      if (negociosRes.error) throw negociosRes.error;

      const rawMembers = membersRes.data || [];
      const badgeRows = await fetchMemberBadgesForIds(rawMembers.map((m) => m.id));
      const withBadges = attachBadgesToMembers(rawMembers, badgeRows);

      setMembers(withBadges);
      setCapitulos(capitulosRes.data || []);
      setClubs((clubsRes.data || []).map(mapClubFromDb));
      setClubSuggestions((suggestionsRes.data || []).map(mapClubSuggestionFromDb));
      setEventos((eventosRes.data || []).map(mapEventoFromDb));
      setRsvps((rsvpsRes.data || []).map(mapRsvpFromDb));
      setTrainings((trainingsRes.data || []).map(mapTrainingFromDb));
      setReels((reelsRes.data || []).map(mapReelFromDb));
      setComments((commentsRes.data || []).map(mapCommentFromDb));
      setReactions((reactionsRes.data || []).map(mapReactionFromDb));
      setCommentReactions((commentReactionsRes.data || []).map(mapCommentReactionFromDb));
      setContentReports(contentReportsRes.error ? [] : (contentReportsRes.data || []));
      setNegociosServicios((negociosRes.data || []).map(mapNegocioFromDb));

      // Current Abanderado cycle + this user's vote (if any)
      const now = new Date();
      const periodo = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const voter = withBadges.find((m) => m.auth_user_id === authUser?.id);

      const { data: abanderadoBadge } = await supabase
        .from('badges')
        .select('id')
        .eq('nombre', 'Abanderado')
        .maybeSingle();

      if (abanderadoBadge?.id) {
        const { data: cycle } = await supabase
          .from('badge_cycles')
          .select('id')
          .eq('badge_id', abanderadoBadge.id)
          .eq('periodo', periodo)
          .maybeSingle();

        setAbanderadoCycleId(cycle?.id || null);

        if (cycle?.id && voter?.id) {
          const { data: myVote } = await supabase
            .from('badge_votes')
            .select('candidate_member_id')
            .eq('cycle_id', cycle.id)
            .eq('voted_by_member_id', voter.id)
            .limit(1)
            .maybeSingle();
          setAbanderadoVote(myVote?.candidate_member_id || null);
        } else {
          setAbanderadoVote(null);
        }
      } else {
        setAbanderadoCycleId(null);
        setAbanderadoVote(null);
      }

      // Keep currentUser badges in sync when directory reloads
      setCurrentUser((prev) => {
        if (!prev) return prev;
        const refreshed = withBadges.find((m) => m.id === prev.id);
        return refreshed ? { ...prev, ...refreshed } : prev;
      });
    } catch (err) {
      console.error('Failed to load directory data:', err);
    } finally {
      setDataLoading(false);
    }
  }, []);

  const applySession = useCallback(async (nextSession) => {
    if (!nextSession?.user) {
      setSession(null);
      setCurrentUser(null);
      setMembers([]);
      setCapitulos([]);
      setClubs([]);
      setClubSuggestions([]);
      setEventos([]);
      setRsvps([]);
      setTrainings([]);
      setReels([]);
      setComments([]);
      setReactions([]);
      setCommentReactions([]);
      setContentReports([]);
      setNegociosServicios([]);
      setAbanderadoVote(null);
      setAbanderadoCycleId(null);
      return;
    }

    try {
      const member = await fetchMemberByAuthUserId(nextSession.user.id);
      // Set both after the member loads so isAuthenticated never flips true with a null currentUser
      setCurrentUser(member);
      setSession(nextSession);
    } catch (err) {
      console.error('Failed to load member for session:', err);
      setSession(null);
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data: { session: existingSession }, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (!mounted) return;
        await applySession(existingSession);
      } catch (err) {
        console.error('Failed to restore session:', err);
        if (mounted) {
          setSession(null);
          setCurrentUser(null);
        }
      } finally {
        if (mounted) setAuthLoading(false);
      }
    };

    init();

    // Avoid deadlock: do not await Supabase calls directly inside the callback
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setTimeout(() => {
        if (!mounted) return;
        applySession(nextSession);
      }, 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  // Load directory once authenticated
  useEffect(() => {
    if (!session) return;
    loadDirectoryData();
  }, [session, loadDirectoryData]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    // Ensure currentUser is set before callers navigate away
    if (data.session?.user) {
      await applySession(data.session);
    }

    return data;
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setSession(null);
    setCurrentUser(null);
  };

  const registerMember = async (data, inviteCode) => {
    const conexion = data.conexion_argentina;
    if (!conexion) {
      throw new Error('Seleccioná tu conexión con Argentina');
    }

    const trimmedInvite = inviteCode?.trim() || '';
    let inviter = null;

    if (trimmedInvite) {
      const { data: inviteRows, error: inviteError } = await supabase
        .rpc('lookup_invite_code', { p_code: trimmedInvite });

      if (inviteError) {
        throw new Error(inviteError.message || 'No se pudo validar el código de invitación');
      }

      inviter = Array.isArray(inviteRows) ? inviteRows[0] : inviteRows;
      if (!inviter?.id) {
        throw new Error('Código de invitación inválido');
      }
    }

    // Mock capitulo ids (e.g. "c1") are not UUIDs — only send real chapter UUIDs
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const capituloId = uuidRe.test(data.capitulo_id) ? data.capitulo_id : null;

    // No session until email is confirmed. Member row is created by DB trigger from this metadata.
    const { data: authData, error: signUpError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        emailRedirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
        data: {
          nombre: data.nombre,
          foto_url: data.foto_url || '',
          capitulo_id: capituloId || '',
          rubro: data.rubro || '',
          ofrezco: data.ofrezco || '',
          busco: data.busco || '',
          whatsapp_number: data.whatsapp_number || '',
          whatsapp_visible: !!data.whatsapp_visible,
          conexion_argentina: conexion,
          ...(trimmedInvite ? { invite_code: trimmedInvite } : {}),
        },
      },
    });

    if (signUpError) {
      const msg = signUpError.message || '';
      if (/already registered|already been registered|user already exists/i.test(msg)) {
        throw new Error('Este email ya está registrado');
      }
      if (/email.*invalid|invalid.*email/i.test(msg)) {
        throw new Error('El email ingresado no es válido');
      }
      if (/password/i.test(msg)) {
        throw new Error(msg);
      }
      throw new Error(msg || 'No se pudo crear la cuenta');
    }

    if (!authData.user) {
      throw new Error('No se pudo crear el usuario de autenticación');
    }

    // With email confirmation enabled there should be no session; clear one if present
    if (authData.session) {
      await supabase.auth.signOut();
    }
    setSession(null);
    setCurrentUser(null);

    return trimmedInvite ? 'approved' : 'pending';
  };

  const toggleRsvp = async (eventId) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const existing = rsvps.find((r) => r.eventId === eventId && r.memberId === currentUser.id);

    if (existing) {
      const { error } = await supabase
        .from('rsvps')
        .delete()
        .eq('evento_id', eventId)
        .eq('member_id', currentUser.id);

      if (error) throw error;
      setRsvps((prev) => prev.filter((r) => !(r.eventId === eventId && r.memberId === currentUser.id)));
      return;
    }

    const { data, error } = await supabase
      .from('rsvps')
      .insert({
        evento_id: eventId,
        member_id: currentUser.id,
      })
      .select('*')
      .single();

    if (error) throw error;
    setRsvps((prev) => [...prev, mapRsvpFromDb(data)]);
  };

  const addEvento = async (newEvento) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const payload = buildEventoInsertPayload(newEvento, currentUser.id);
    if (Number.isNaN(Date.parse(payload.fecha))) {
      throw new Error('Fecha inválida');
    }
    if (payload.modalidad === 'presencial' && !payload.ubicacion) {
      throw new Error('La ubicación es obligatoria para eventos presenciales');
    }
    if (payload.modalidad === 'virtual' && !payload.link_virtual) {
      throw new Error('El enlace es obligatorio para eventos virtuales');
    }

    const { data, error } = await supabase
      .from('eventos')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw error;

    const mapped = mapEventoFromDb(data);
    setEventos((prev) => {
      const next = [...prev, mapped];
      next.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
      return next;
    });
    return mapped;
  };

  const addClub = async (newClub) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const payload = {
      nombre: newClub.nombre,
      descripcion: newClub.descripcion || null,
      tipo: clubTipoToDb(newClub.tipo),
      capitulo_id: newClub.capitulo_id || null,
      foto_url: newClub.foto_portada || newClub.foto_url || null,
      created_by: currentUser.id,
      destacado: !!newClub.destacado,
    };

    const { data, error } = await supabase
      .from('clubs')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw error;

    const mapped = mapClubFromDb(data);
    setClubs((prev) => [mapped, ...prev]);
    return mapped;
  };

  const suggestClub = async (suggestion) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const payload = {
      nombre_sugerido: suggestion.nombre,
      descripcion: suggestion.descripcion || null,
      tipo: clubTipoToDb(suggestion.tipo),
      capitulo_id: suggestion.capitulo_id || null,
      suggested_by: currentUser.id,
    };

    const { data, error } = await supabase
      .from('club_suggestions')
      .insert(payload)
      .select('*, suggested_by_member:members!suggested_by(nombre)')
      .single();

    if (error) throw error;

    const mapped = mapClubSuggestionFromDb(data);
    setClubSuggestions((prev) => [mapped, ...prev]);
    return mapped;
  };

  const removeClubSuggestion = async (id) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const { data, error } = await supabase
      .from('club_suggestions')
      .update({
        status: 'rejected',
        reviewed_by: currentUser.id,
      })
      .eq('id', id)
      .select('id');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo descartar la sugerencia (posible política RLS)');
    }

    setClubSuggestions((prev) => prev.filter((cs) => cs.id !== id));
  };

  const approveClubSuggestion = async (suggestionId, clubData) => {
    const club = await addClub(clubData);

    const { data, error } = await supabase
      .from('club_suggestions')
      .update({
        status: 'approved',
        reviewed_by: currentUser.id,
        created_club_id: club.id,
      })
      .eq('id', suggestionId)
      .select('id');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo aprobar la sugerencia (posible política RLS)');
    }

    setClubSuggestions((prev) => prev.filter((cs) => cs.id !== suggestionId));
    return club;
  };

  // Comments & Reactions
  const toggleReaction = async (itemId, itemType) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');
    if (!['evento', 'club'].includes(itemType)) {
      throw new Error('Tipo de reacción no soportado');
    }

    const existing = reactions.find(
      (r) => r.item_id === itemId && r.member_id === currentUser.id && r.item_type === itemType
    );

    if (existing) {
      const { error } = await supabase
        .from('reactions')
        .delete()
        .eq('id', existing.id)
        .eq('member_id', currentUser.id);
      if (error) throw error;
      setReactions((prev) => prev.filter((r) => r.id !== existing.id));
      return;
    }

    const { data, error } = await supabase
      .from('reactions')
      .insert({
        reactable_type: itemType,
        reactable_id: itemId,
        member_id: currentUser.id,
      })
      .select('*')
      .single();

    if (error) throw error;
    setReactions((prev) => [...prev, mapReactionFromDb(data)]);
  };

  const addComment = async (itemId, itemType, text) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');
    if (!['evento', 'club'].includes(itemType)) {
      throw new Error('Tipo de comentario no soportado');
    }
    const contenido = text?.trim();
    if (!contenido) throw new Error('El comentario no puede estar vacío');

    const { data, error } = await supabase
      .from('comments')
      .insert({
        commentable_type: itemType,
        commentable_id: itemId,
        member_id: currentUser.id,
        contenido,
      })
      .select('*')
      .single();

    if (error) throw error;
    setComments((prev) => [...prev, mapCommentFromDb(data)]);
  };

  const toggleCommentReaction = async (commentId, type) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');
    const tipo = commentReactionToDb(type);
    const existing = commentReactions.find(
      (cr) => cr.comment_id === commentId && cr.member_id === currentUser.id
    );

    if (existing) {
      if (existing.type === type) {
        const { error } = await supabase
          .from('comment_reactions')
          .delete()
          .eq('id', existing.id)
          .eq('member_id', currentUser.id);
        if (error) throw error;
        setCommentReactions((prev) => prev.filter((cr) => cr.id !== existing.id));
        return;
      }

      const { data, error } = await supabase
        .from('comment_reactions')
        .update({ tipo })
        .eq('id', existing.id)
        .eq('member_id', currentUser.id)
        .select('*');

      if (error) throw error;
      if (!data?.length) {
        throw new Error('No se pudo actualizar la reacción');
      }
      setCommentReactions((prev) =>
        prev.map((cr) => (cr.id === existing.id ? mapCommentReactionFromDb(data[0]) : cr))
      );
      return;
    }

    const { data, error } = await supabase
      .from('comment_reactions')
      .insert({
        comment_id: commentId,
        member_id: currentUser.id,
        tipo,
      })
      .select('*')
      .single();

    if (error) throw error;
    setCommentReactions((prev) => [...prev, mapCommentReactionFromDb(data)]);
  };

  const submitTraining = async (input) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const presenterMemberId = input.presentado_por_member_id || currentUser.id;
    const presenterExterno = input.presentado_por_externo?.trim() || null;

    const payload = {
      titulo: input.titulo,
      descripcion: input.descripcion || null,
      categoria: input.categoria || null,
      embed_url: input.embed_url || null,
      duracion: input.duracion || null,
      publitio_id: input.publitio_id || null,
      presentado_por_member_id: presenterExterno ? null : presenterMemberId,
      presentado_por_externo: presenterExterno,
      submitted_by: currentUser.id,
      status: 'pending',
    };

    const { data, error } = await supabase
      .from('trainings')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw error;

    const mapped = mapTrainingFromDb(data);
    setTrainings((prev) => [mapped, ...prev]);
    return mapped;
  };

  const submitReel = async (input) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const payload = {
      titulo: input.titulo,
      embed_url: input.embed_url || input.thumbnail_url || null,
      publitio_id: input.publitio_id || null,
      training_id: input.training_id || input.linked_training_id || null,
      submitted_by: currentUser.id,
      status: 'pending',
    };

    const { data, error } = await supabase
      .from('reels')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw error;

    const mapped = mapReelFromDb(data);
    setReels((prev) => [mapped, ...prev]);
    return mapped;
  };

  const approveMember = async (id) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const { data, error } = await supabase
      .from('members')
      .update({ status: 'approved' })
      .eq('id', id)
      .select('*');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo aprobar el miembro (posible política RLS — solo admin)');
    }

    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...data[0] } : m)));
  };

  const rejectMember = async (id) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const { data, error } = await supabase
      .from('members')
      .update({ status: 'rejected' })
      .eq('id', id)
      .select('id');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo rechazar el miembro (posible política RLS — solo admin)');
    }

    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'rejected' } : m)));
  };
  const contentTableForType = (type) => {
    if (type === 'training') return 'trainings';
    if (type === 'reel') return 'reels';
    if (type === 'negocio') return 'negocios_servicios';
    return null;
  };

  const approveContent = async (type, id) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');
    const table = contentTableForType(type);
    if (!table) throw new Error('Tipo de contenido no soportado');

    const payload =
      type === 'negocio'
        ? { status: 'approved' }
        : { status: 'approved', reviewed_by: currentUser.id };

    const { data, error } = await supabase
      .from(table)
      .update(payload)
      .eq('id', id)
      .select('*');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo aprobar el contenido (posible política RLS)');
    }

    if (type === 'training') {
      setTrainings((prev) =>
        prev.map((t) => (t.id === id ? mapTrainingFromDb(data[0]) : t))
      );
    } else if (type === 'reel') {
      setReels((prev) =>
        prev.map((r) => (r.id === id ? mapReelFromDb(data[0]) : r))
      );
    } else {
      setNegociosServicios((prev) =>
        prev.map((n) => (n.id === id ? mapNegocioFromDb(data[0]) : n))
      );
    }
  };

  const rejectContent = async (type, id) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');
    const table = contentTableForType(type);
    if (!table) throw new Error('Tipo de contenido no soportado');

    const payload =
      type === 'negocio'
        ? { status: 'rejected' }
        : { status: 'rejected', reviewed_by: currentUser.id };

    const { data, error } = await supabase
      .from(table)
      .update(payload)
      .eq('id', id)
      .select(type === 'negocio' ? '*' : 'id');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo rechazar el contenido (posible política RLS)');
    }

    if (type === 'training') {
      setTrainings((prev) =>
        prev.map((t) => (t.id === id ? { ...t, status: 'rejected', reviewed_by: currentUser.id } : t))
      );
    } else if (type === 'reel') {
      setReels((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: 'rejected', reviewed_by: currentUser.id } : r))
      );
    } else {
      setNegociosServicios((prev) =>
        prev.map((n) => (n.id === id ? mapNegocioFromDb(data[0]) : n))
      );
    }
  };

  const createNegocioServicio = async ({
    form,
    logoFile,
    galleryFiles = [],
    existingFotos = [],
  }) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    let created = null;
    let lastError = null;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const slug = makeNegocioSlug(form.nombre);
      const payload = buildNegocioInsertPayload({
        memberId: currentUser.id,
        form,
        slug,
      });
      const { data, error } = await supabase
        .from('negocios_servicios')
        .insert(payload)
        .select('*')
        .single();
      if (!error) {
        created = data;
        break;
      }
      lastError = error;
      if (error.code !== '23505') break; // unique_violation on slug
    }
    if (!created) throw lastError || new Error('No se pudo crear el listing');

    const listingId = created.id;
    let logoUrl = null;
    if (logoFile) {
      logoUrl = await uploadNegocioFoto(listingId, logoFile, 'logo');
    }

    const uploadedGallery = [];
    for (const file of galleryFiles.slice(0, NEGOCIO_MAX_FOTOS)) {
      const path = await uploadNegocioFoto(listingId, file, 'galeria');
      uploadedGallery.push(path);
    }
    const fotos = [...existingFotos, ...uploadedGallery].slice(0, NEGOCIO_MAX_FOTOS);

    let items = Array.isArray(created.items) ? [...created.items] : [];
    if (form.tipo === 'negocio' && Array.isArray(form.items)) {
      items = [];
      for (const item of form.items) {
        if (!String(item.nombre || '').trim()) continue;
        let fotoPath = item.foto_url || null;
        if (item._file) {
          fotoPath = await uploadNegocioFoto(listingId, item._file, 'item');
        }
        items.push({
          nombre: String(item.nombre).trim(),
          descripcion: String(item.descripcion || '').trim(),
          ...(item.precio != null && String(item.precio).trim() !== ''
            ? { precio: String(item.precio).trim() }
            : {}),
          ...(fotoPath ? { foto_url: fotoPath } : {}),
        });
      }
    }

    const { data: updated, error: updateError } = await supabase
      .from('negocios_servicios')
      .update({ logo_url: logoUrl, fotos, items })
      .eq('id', listingId)
      .select('*')
      .single();

    if (updateError) throw updateError;

    const mapped = mapNegocioFromDb(updated);
    setNegociosServicios((prev) => [mapped, ...prev]);
    return mapped;
  };

  const updateNegocioServicio = async ({
    listingId,
    form,
    logoFile,
    removeLogo = false,
    existingLogoUrl = null,
    galleryFiles = [],
    existingFotos = [],
  }) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');
    if (!listingId) throw new Error('Falta el id del listing');

    let logoUrl = removeLogo ? null : existingLogoUrl;
    if (logoFile) {
      logoUrl = await uploadNegocioFoto(listingId, logoFile, 'logo');
    }

    const uploadedGallery = [];
    for (const file of galleryFiles) {
      if (existingFotos.length + uploadedGallery.length >= NEGOCIO_MAX_FOTOS) break;
      const path = await uploadNegocioFoto(listingId, file, 'galeria');
      uploadedGallery.push(path);
    }
    const fotos = [...existingFotos, ...uploadedGallery].slice(0, NEGOCIO_MAX_FOTOS);

    let items = [];
    if (form.tipo === 'negocio' && Array.isArray(form.items)) {
      for (const item of form.items) {
        if (!String(item.nombre || '').trim()) continue;
        let fotoPath = item.foto_url || item._existingFoto || null;
        if (item._file) {
          fotoPath = await uploadNegocioFoto(listingId, item._file, 'item');
        }
        items.push({
          nombre: String(item.nombre).trim(),
          descripcion: String(item.descripcion || '').trim(),
          ...(item.precio != null && String(item.precio).trim() !== ''
            ? { precio: String(item.precio).trim() }
            : {}),
          ...(fotoPath ? { foto_url: fotoPath } : {}),
        });
      }
    }

    const payload = {
      ...buildNegocioUpdatePayload(form),
      logo_url: logoUrl,
      fotos,
      items,
    };

    const { data, error } = await supabase
      .from('negocios_servicios')
      .update(payload)
      .eq('id', listingId)
      .select('*')
      .single();

    if (error) throw error;

    const mapped = mapNegocioFromDb(data);
    setNegociosServicios((prev) => prev.map((n) => (n.id === listingId ? mapped : n)));
    return mapped;
  };

  const deleteNegocioServicio = async (id) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const { data, error } = await supabase
      .from('negocios_servicios')
      .delete()
      .eq('id', id)
      .select('id');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo eliminar (posible política RLS)');
    }

    setNegociosServicios((prev) => prev.filter((n) => n.id !== id));
  };

  const flagContent = async (type, id, motivo) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');
    if (!['training', 'reel'].includes(type)) {
      throw new Error('Tipo de contenido no soportado');
    }
    const reason = motivo?.trim();
    if (!reason) throw new Error('Ingresá un motivo para el reporte');

    const table = type === 'training' ? 'trainings' : 'reels';
    const { data, error } = await supabase
      .from(table)
      .update({ status: 'flagged', reviewed_by: currentUser.id })
      .eq('id', id)
      .select('*');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo marcar el contenido (posible política RLS — solo moderador/admin)');
    }

    const { data: report, error: reportError } = await supabase
      .from('content_reports')
      .insert({
        content_type: type,
        content_id: id,
        reported_by: currentUser.id,
        motivo: reason,
      })
      .select('*')
      .single();

    if (reportError) throw reportError;

    if (type === 'training') {
      setTrainings((prev) =>
        prev.map((t) => (t.id === id ? mapTrainingFromDb(data[0]) : t))
      );
    } else {
      setReels((prev) =>
        prev.map((r) => (r.id === id ? mapReelFromDb(data[0]) : r))
      );
    }
    setContentReports((prev) => [report, ...prev]);
    return report;
  };

  // Flagged queue from content_reports + still-flagged trainings/reels
  const reportedContent = contentReports
    .map((report) => {
      const item =
        report.content_type === 'training'
          ? trainings.find((t) => t.id === report.content_id)
          : reels.find((r) => r.id === report.content_id);
      if (!item || item.status !== 'flagged') return null;
      return {
        id: report.id,
        content_id: report.content_id,
        type: report.content_type,
        titulo: item.titulo,
        reported_by_name:
          members.find((m) => m.id === report.reported_by)?.nombre || 'Miembro',
        reason: report.motivo,
      };
    })
    .filter(Boolean);

  const reportedMods = members
    .filter((m) => m.flagged_for_suspension_by)
    .map((m) => ({
      id: m.id,
      moderator_id: m.id,
      moderator_name: m.nombre,
      reported_by_name:
        members.find((x) => x.id === m.flagged_for_suspension_by)?.nombre || 'Miembro',
      reason: 'Marcado para suspensión',
      flagged_at: m.flagged_for_suspension_at,
    }));

  // Ignorar reporte → restore approved; Eliminar → reject
  const clearReport = async (reportId) => {
    const report = reportedContent.find((r) => r.id === reportId);
    if (!report) return;
    await approveContent(report.type, report.content_id);
  };

  const dismissModReport = async (memberId) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const { data, error } = await supabase
      .from('members')
      .update({
        flagged_for_suspension_by: null,
        flagged_for_suspension_at: null,
      })
      .eq('id', memberId)
      .select('id');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo descartar el flag (posible política RLS — solo admin)');
    }

    setMembers((prev) =>
      prev.map((m) =>
        m.id === memberId
          ? { ...m, flagged_for_suspension_by: null, flagged_for_suspension_at: null }
          : m
      )
    );
  };

  const suspendMember = async (memberId) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');

    const { data, error } = await supabase
      .from('members')
      .update({
        status: 'suspended',
        flagged_for_suspension_by: null,
        flagged_for_suspension_at: null,
      })
      .eq('id', memberId)
      .select('*');

    if (error) throw error;
    if (!data?.length) {
      throw new Error('No se pudo suspender al miembro (posible política RLS — solo admin)');
    }

    setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, ...data[0] } : m)));
  };

  // Profile fields the member can edit on their own row (members_update_own)
  const updateMyProfile = async (form) => {
    if (!session?.user?.id) throw new Error('Debés iniciar sesión');
    if (!currentUser?.id) throw new Error('No se encontró el perfil del miembro');

    const payload = buildProfileUpdatePayload(form);
    if (!payload.nombre) throw new Error('El nombre es obligatorio');

    const { data, error } = await supabase
      .from('members')
      .update(payload)
      .eq('auth_user_id', session.user.id)
      .select('*');

    if (error) throw error;
    // UPDATE without matching SELECT/USING returns 0 rows (no error) — treat as failure
    if (!data?.length) {
      throw new Error(
        'No se pudo guardar el perfil (posible política RLS — revisá members_update_own)'
      );
    }

    const updated = data[0];
    setMembers((prev) =>
      prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m))
    );
    setCurrentUser((prev) =>
      prev ? { ...prev, ...updated, badges: prev.badges } : prev
    );
    return updated;
  };

  // Badge Management — toggles is_featured on member_badges rows
  const updateFeaturedBadges = async (memberId, newBadgesArray) => {
    const previous =
      memberId === currentUser?.id
        ? currentUser.badges
        : members.find((m) => m.id === memberId)?.badges || [];

    // Optimistic UI
    setMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, badges: newBadgesArray } : m))
    );
    if (currentUser?.id === memberId) {
      setCurrentUser((prev) => ({ ...prev, badges: newBadgesArray }));
    }

    try {
      const updates = newBadgesArray.filter((b) => {
        const before = previous.find((p) => p.memberBadgeId === b.memberBadgeId);
        return before && before.featured !== b.featured;
      });

      for (const badge of updates) {
        if (!badge.memberBadgeId) {
          throw new Error('Missing member_badges id for featured toggle');
        }
        const { data, error } = await supabase
          .from('member_badges')
          .update({ is_featured: badge.featured })
          .eq('id', badge.memberBadgeId)
          .eq('member_id', memberId)
          .select('id');

        if (error) throw error;
        // RLS with no UPDATE policy returns 0 rows (no error) — treat as failure
        if (!data?.length) {
          throw new Error(
            'No se pudo actualizar la insignia (posible política RLS faltante en member_badges)'
          );
        }
      }
    } catch (err) {
      console.error('updateFeaturedBadges failed:', err);
      // Revert optimistic update
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, badges: previous } : m))
      );
      if (currentUser?.id === memberId) {
        setCurrentUser((prev) => ({ ...prev, badges: previous }));
      }
      throw err;
    }
  };

  const castAbanderadoVote = async (memberId) => {
    if (!currentUser?.id) throw new Error('Debés iniciar sesión');
    if (abanderadoVote) return;
    if (memberId === currentUser.id) {
      throw new Error('No podés votarte a vos mismo');
    }

    let cycleId = abanderadoCycleId;
    if (!cycleId) {
      const now = new Date();
      const periodo = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
      const { data: abanderadoBadge } = await supabase
        .from('badges')
        .select('id')
        .eq('nombre', 'Abanderado')
        .maybeSingle();
      if (!abanderadoBadge?.id) {
        throw new Error('Insignia Abanderado no configurada');
      }
      const { data: cycle } = await supabase
        .from('badge_cycles')
        .select('id')
        .eq('badge_id', abanderadoBadge.id)
        .eq('periodo', periodo)
        .maybeSingle();
      if (!cycle?.id) {
        throw new Error(`No hay ciclo de votación para ${periodo}. Pedile a un admin que lo cree.`);
      }
      cycleId = cycle.id;
      setAbanderadoCycleId(cycleId);
    }

    const { data, error } = await supabase
      .from('badge_votes')
      .insert({
        cycle_id: cycleId,
        candidate_member_id: memberId,
        voted_by_member_id: currentUser.id,
      })
      .select('candidate_member_id')
      .single();

    if (error) throw error;
    setAbanderadoVote(data.candidate_member_id);
  };

  return (
    <AppContext.Provider value={{
      currentUser, isAuthenticated, authLoading, dataLoading, login, logout, registerMember,
      members, trainings, reels, eventos, clubs, clubSuggestions, negociosServicios,
      reportedContent, reportedMods, rsvps, comments, reactions, commentReactions,
      toggleRsvp, addClub, suggestClub, removeClubSuggestion, approveClubSuggestion, addEvento,
      submitTraining, submitReel,
      createNegocioServicio, updateNegocioServicio, deleteNegocioServicio,
      toggleReaction, addComment, toggleCommentReaction,
      approveMember, rejectMember, approveContent, rejectContent, flagContent, clearReport, dismissModReport, suspendMember,
      CAPITULOS: capitulos, RUBROS, BADGES_INFO, CATEGORIAS_ACADEMIA,
      abanderadoVote, castAbanderadoVote, updateMyProfile, updateFeaturedBadges, refreshDirectory: loadDirectoryData
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
